import { Component, Input, OnInit, ElementRef, AfterViewInit, TemplateRef } from '@angular/core';

interface IBaseStyles {
  show?: boolean;
  width?: string;
  height?: string;
  background?: string;
  outline?: boolean;
}

interface IAvatar extends IBaseStyles {
  shape?: 'rounded' | 'circle';
  position?: Position;
  srcTemplate?: TemplateRef<null>;
}

interface IList extends IBaseStyles {
  spacing?: number;
  rows?: number;
  rowSpacing?: number;
}

enum Position {
  right = 'right',
  left = 'left',
}

enum Shape {
  circle = 'circle',
  rounded = 'rounded',
}

@Component({
  selector: 'skeleton',
  styleUrls: ['./skeleton.component.scss'],
  templateUrl: './skeleton.component.html',
})

export class SkeletonComponent implements OnInit, AfterViewInit {
  @Input() public count: number = 1;
  @Input() public avatar: IAvatar;
  @Input() public title: IBaseStyles;
  @Input() public shouldShowAnimation: boolean = true;
  @Input('paragraph') public list: IList;
  @Input('text-width') public textContentWidth: string; // can be any unit like px, em, %
  public readonly position: typeof Position = Position;
  public readonly shape: typeof Shape = Shape;
  public readonly skeletons: number[] = [];
  public readonly paragraphs: number[] = [];
  private el: HTMLElement;
  private readonly defaultAvatar: IAvatar = {
    show: true,
    shape: 'circle',
    position: Position.left,
    width: '28px',
    height: '28px',
    srcTemplate: null,
  };
  private readonly defaultTitle: IBaseStyles = {
    show: true,
    width: '100%',
    height: '7px',
  };
  private readonly defaultList: IList = {
    show: true,
    spacing: 0,
    width: '100%',
    height: '7px',
    rows: 3,
    rowSpacing: 10,
  };

  constructor(elRef: ElementRef) {
    this.el = elRef.nativeElement;
  }

  public ngOnInit(): void {
    this.avatar = { ...this.defaultAvatar, ...this.avatar };
    this.title = { ...this.defaultTitle, ...this.title };
    this.list = { ...this.defaultList, ...this.list };
    this.skeletons.length = this.count;
    this.paragraphs.length = this.list.rows;
  }

  public ngAfterViewInit(): void {
    this.setStyleAttributes();
  }

  /**
   * Set styles which also incorproates the count of the skeletons in single component
   */
  private setStyleAttributes(): void {
    Array.from(this.el.querySelectorAll('.avatar'))
      .forEach((avatar: HTMLElement): void => {
        avatar.setAttribute('style', `
          width: ${this.avatar.width};
          height: ${this.avatar.height};
          ${this.avatar.background ? `background-color: ${this.avatar.background};` : ''}
        `);
      });

    Array.from(this.el.querySelectorAll('.title'))
      .forEach((title: HTMLElement): void => {
        title.classList.add(`mb-${this.list.spacing}`);
        title.setAttribute('style', `
          width: ${this.textContentWidth || this.title.width};
          height: ${this.title.height};
          ${this.title.background ? `background-color: ${this.title.background};` : ''}
        `);
      });

    Array.from(this.el.querySelectorAll('.list-item'))
      .forEach((listItem: HTMLElement, index: number): void => {
        const isLastRow: boolean = ((index + 1) % this.list.rows) === 0;
        listItem.setAttribute('style', `
          max-width: ${isLastRow ? '70%' : this.list.width};
          height: ${this.list.height};
          margin-top: ${this.list.rowSpacing}px;
          ${this.list.background ? `background-color: ${this.list.background};` : ''}
        `);
      });
  }
}
